import {mapValues, getMinutes, max, sumBy, minBy, fullday, createSelector} from 'helpers';
import {subroutinesById} from 'store/activities/selectors';
import {day} from './day';

const derivedDurations = createSelector(
  [subroutinesById],
  (subroutinesById) => {
    return mapValues(subroutinesById, items => sumBy(items, item => item.duration || 30));
  }
);

export const dayTimetable = createSelector(
  [day, derivedDurations],
  (day, derivedDurations) => {
    let groups = [];
    day.forEach(activity => {
      if (activity.time && getMinutes(activity.time) !== null) {
        const duration = max([30, activity.duration || 30, derivedDurations[activity.id]]);
        activity = {
          ...activity,
          end: Math.min(getMinutes(activity.time) + duration, fullday),
          time: getMinutes(activity.time)
        }
        let group = {
          time: activity.time,
          end: activity.end,
          items: [activity],
        };
        const intersected = intersect(groups, activity);
        if (intersected.length) {
          group = merge([
            ...intersected.map(i => groups[i]),
            group
          ]);
          groups = groups.filter((group, index) => intersected.indexOf(index) == -1);
        }
        groups.push(group);
      }
    });
    return groups.reduce((carry, group) => [
      ...carry,
      ...wrap(collapse(group.items)),
    ], []);
  },
  {memoizeOptions: {maxSize: 100}},
);

function intersect(groups, target) {
  const results = [];
  let i = groups.length;
  while (i--) {
    if (target.time >= groups[i].end || target.end <= groups[i].time) {
      continue;
    }
    results.push(i);
  }
  return results;
}

function merge(groups) {
  return {
    time: minBy(groups, 'time').time,
    end: minBy(groups, 'end').end,
    items: groups.reduce(concatItems, []),
  }
}

function concatItems(array, {items}) {
  return array.concat(items);
}

function collapse(items) {
  const to_collapse = [];
  for (let i = items.length - 1; i > 0; i--) {
    for (let j = 0; j < items.length; j++) {
      if (i != j && items[i].time > items[j].end || items[i].end < items[j].time) {
        to_collapse.push([i, j]);
      }
    }
  }

  const merged = [];
  const collapsed = [];
  to_collapse.forEach(pair => {
    if (collapsed.indexOf(pair[0]) == -1 && collapsed.indexOf(pair[1]) == -1) {
      collapsed.push(pair[0]);
      collapsed.push(pair[1]);
      merged.push({
        time: Math.min.apply(null, pair.map(i => items[i].time)),
        collapsed: pair.map(i => items[i])
      });
    }
  });

  return [
    ...items.filter((activity, index) => collapsed.indexOf(index) == -1),
    ...merged
  ];
}

function wrap(items) {
  return items.sort(sortByTime).map((item, index) =>
    (item.collapsed || [item]).map(item => ({
      ...item,
      style: {
        position: 'absolute',
        left: 100 * index / items.length + '%',
        top: 100 * item.time / fullday + '%',
        bottom: 100 * (fullday - item.end) / fullday + '%',
        right: 100 * (items.length - index - 1) / items.length + '%',
      },
    }))
  ).reduce((carry, activities) => [...carry, ...activities], []);
}

function sortByTime(a, b) {
  return a.time - b.time;
}
